import { getSessionDataFromUrl } from './urlDecoder';
import { applySessionMaestroSettings } from './maestroSessionSettings';
import { setViewOverride } from './viewMode';

const STORAGE_KEYS = {
  SESSION_DATA: 'aedrive_session_data',
  DEVICE_ID: 'aedrive_device_id',
  ORG_ID: 'aedrive_org_id',
  ACCESS_KEY: 'aedrive_access_key',
  VALIDATION_SHOWN: 'aedrive_validation_shown',
};

export const getOrCreateDeviceId = () => {
  let deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
  }
  return deviceId;
};

export const initializeSession = (url = window.location.href) => {
  const sessionData = getSessionDataFromUrl(url);

  // No fresh ?session= payload in this URL — fall back to whatever's already
  // stored (normal in-app navigation after the URL has been cleaned).
  if (!sessionData) {
    return getStoredSession();
  }

  // A fresh ?session= payload always wins over a stored session — otherwise
  // opening a second short URL (a different user/session) in the same
  // browser would silently keep using whoever's session was stored first.

  const deviceId = sessionData.deviceId || getOrCreateDeviceId();

  const session = {
    orgId: sessionData.orgId || sessionData.org_id || null,
    accessKey: sessionData.accessKey || sessionData.access_key || null,
    refreshToken: sessionData.refreshToken || sessionData.refresh_token || null,
    sessionStartTime: sessionData.sessionStartTime || Date.now(),
    deviceId: deviceId,
    userId: sessionData.userId || sessionData.user_id || null,
    username: sessionData.username || sessionData.user_name || null,
    email: sessionData.email || null,
    role: sessionData.role || null,
    os: sessionData.os || null,
    browser: sessionData.browser || null,
    screenResolution: sessionData.screenResolution || null,
    platform: sessionData.platform || null,
    ...sessionData,
  };
  
  if (session.refresh_token && !session.refreshToken) {
    session.refreshToken = session.refresh_token;
  }

  if (!session.orgId) {
    return null;
  }

  try {
    saveSessionToStorage(session);
    
    const verify = getStoredSession();
    if (!verify || !verify.orgId) {
      try {
        localStorage.setItem(STORAGE_KEYS.SESSION_DATA, JSON.stringify(session));
        const verifyAgain = getStoredSession();
        if (!verifyAgain || !verifyAgain.orgId) {
          return null;
        }
      } catch (retryError) {
        return null;
      }
    }
  } catch (error) {
    return null;
  }

  // The host's Maestro setup (if any) and the session's renderer ride this
  // payload — hand them to the Maestro component via its persisted settings
  // before it mounts.
  applySessionMaestroSettings(session);

  applySessionFixture(session);

  // A link for a session with an assigned app lands in that app — drop a view
  // a privileged user picked earlier with the footer toggle.
  if (session.applicationFlavour) setViewOverride(null);

  return session;
};

/**
 * Make the payload's fixture the stored fixture selection.
 *
 * The fixture only reaches the route when the session also has a renderer
 * (/fixture/:f/renderer/:r/ is the only deep-link shape). A session without
 * one — or whose renderer was deleted — lands on the bare org path, and
 * TXLivePage then fell back to this browser's own stored fixture, or the
 * season's first fixture in a browser with none: the same link opened a
 * different match per browser. The stored competition/season belong to the
 * old selection, so they go too — TXLivePage resolves them from the fixture.
 */
export const applySessionFixture = (session) => {
  const fixtureId = session?.fixtureId || session?.fixture_id;
  if (!fixtureId) return;
  try {
    if (localStorage.getItem('selectedFixtureId') === fixtureId) return;
    localStorage.setItem('selectedFixtureId', fixtureId);
    localStorage.removeItem('selectedFixture');
    localStorage.removeItem('selectedCompetitionId');
    localStorage.removeItem('selectedSeasonId');
  } catch {
    // Storage unavailable — TXLivePage falls back to its own selection.
  }
};

export const saveSessionToStorage = (session) => {
  try {
    const sessionJson = JSON.stringify(session);
    localStorage.setItem(STORAGE_KEYS.SESSION_DATA, sessionJson);
    
    const verify = localStorage.getItem(STORAGE_KEYS.SESSION_DATA);
    if (!verify) {
      throw new Error('Session data was not saved');
    }
    
    if (session.orgId) {
      localStorage.setItem(STORAGE_KEYS.ORG_ID, session.orgId);
    }
    if (session.accessKey) {
      localStorage.setItem(STORAGE_KEYS.ACCESS_KEY, session.accessKey);
    }
    if (session.deviceId) {
      localStorage.setItem(STORAGE_KEYS.DEVICE_ID, session.deviceId);
    }
    if (session.refreshToken || session.refresh_token) {
      const refreshToken = session.refreshToken || session.refresh_token;
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('aedrive_refresh_token', refreshToken);
    }
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.error('LocalStorage quota exceeded');
    }
    throw error;
  }
};

export const getStoredSession = () => {
  try {
    const sessionData = localStorage.getItem(STORAGE_KEYS.SESSION_DATA);
    if (!sessionData) {
      return null;
    }
    return JSON.parse(sessionData);
  } catch (error) {
    console.error('Error reading session from storage:', error);
    return null;
  }
};

export const getCurrentSession = (url = window.location.href) => {
  const stored = getStoredSession();
  if (stored) {
    return stored;
  }
  return initializeSession(url);
};

export const shouldShowValidation = (session) => {
  if (!session || !session.orgId) {
    return false;
  }

  const validationShown = localStorage.getItem(STORAGE_KEYS.VALIDATION_SHOWN);
  const lastSessionId = localStorage.getItem(`${STORAGE_KEYS.VALIDATION_SHOWN}_session`);
  const currentSessionId = `${session.orgId}_${session.deviceId}`;
  
  if (!validationShown || lastSessionId !== currentSessionId) {
    return true;
  }
  
  return false;
};

export const markValidationShown = (session) => {
  if (!session || !session.orgId) {
    return;
  }
  
  const currentSessionId = `${session.orgId}_${session.deviceId}`;
  localStorage.setItem(STORAGE_KEYS.VALIDATION_SHOWN, 'true');
  localStorage.setItem(`${STORAGE_KEYS.VALIDATION_SHOWN}_session`, currentSessionId);
};


// OPUS-901/902: exchange a secure ?h= handoff (server-minted, short-TTL) for a
// real session. The refresh token arrives in the response body, never the URL.
// Returns the stored session on success. On failure returns a discriminated
// error object instead of null so callers can tell a network failure (worth
// retrying) apart from an invalid/expired handoff (not worth retrying):
//   { error: 'network', message } | { error: 'invalid', message }
// OPUS-1106: previously any failure (network or invalid) collapsed to `null`,
// so a dropped connection was indistinguishable from a bad link and the UI
// had no way to offer a retry.
export const exchangeHandoffFromUrl = async () => {
  const params = new URLSearchParams(window.location.search);
  const handoff = params.get('h');
  if (!handoff) return null;
  const base = process.env.REACT_APP_USER_API_URL;
  if (!base) return null;

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { error: 'network', message: 'Unable to connect. Check your internet connection.' };
  }

  // OPUS-902 fix: user-mgmt mounts handoff under /v1. Here REACT_APP_USER_API_URL
  // has no /v1 suffix (Drive appends it elsewhere), so normalise to always hit
  // /v1/handoff/exchange — previously this 404'd ("No session found").
  const apiBase = base.replace(/\/v1\/?$/, '');

  let res;
  try {
    res = await fetch(`${apiBase}/v1/handoff/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handoff }),
    });
  } catch (fetchErr) {
    return { error: 'network', message: 'Unable to connect. Check your internet connection.' };
  }

  try {
    const data = await res.json().catch(() => ({}));
    const d = data && data.data;
    if (!res.ok || !d || !d.accessToken || !d.orgId) {
      return { error: 'invalid', message: 'This link is invalid or no longer available.' };
    }
    const session = {
      orgId: d.orgId,
      accessKey: d.accessToken,
      refreshToken: d.refreshToken,
      role: d.role || null,
      userId: d.userId || null,
      username: d.user_name || null,
      email: d.email || null,
      deviceId: getOrCreateDeviceId(),
    };
    saveSessionToStorage(session);
    params.delete('h');
    const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '') + window.location.hash;
    window.history.replaceState({}, '', clean);
    return session;
  } catch (_) {
    return { error: 'invalid', message: 'This link is invalid or no longer available.' };
  }
};
